"use client";

import { OESTRA_QUIZ_ENTRY_HREF } from "~/constants/oestraLandingPages";
import { useCallback } from "react";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { QuizCTA as QuizLink } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import GettingStartedCarouselStepCard from "~/componentsDS/GettingStartedCarouselStepCard";
import Container from "~/componentsDS/Layout/Container";
import Heading from "~/componentsDS/Typography/Heading";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { STEPS } from "./data";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import Reveal from "~/componentsDS/Reveal";

const GetStartedSection = () => {
    const sendEvent = useTrackEvent();

    const onSelect = useCallback((emblaApi: CarouselApi) => {
        if (!emblaApi) return;

        const index = emblaApi.selectedScrollSnap();
        const item = STEPS[index];

        if (!item) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "how_it_works",
                meta_data: {
                    image_title: item.title,
                    image_count: index + 1,
                },
            },
        });
    }, [sendEvent]);

    return (
        <section className="overflow-hidden pt-16 pb-8 text-primary500 md:pb-16 lg:py-24">
            <Container>
                <Reveal>
                    <Heading as="h2" size="sm" sizes={{ lg: "md" }} className="mb-6 lg:mb-10 lg:text-center">
                        <span className="hidden lg:inline">How to get started?</span>
                        <span className="lg:hidden">Getting started is simple</span>
                    </Heading>
                </Reveal>

                <div className="w-[100vw] md:w-full overflow-hidden mb-4 md:mb-10 max-md:-mx-6 max-md:px-6">
                    <Carousel
                        onSelect={onSelect}
                        loop={false}
                        variant="compact"
                        compactPaginationAlign="right"
                        compactPaginationBg="bg-trueBlack/10 backdrop-blur-[25px]"
                        compactPaginationDotColor="bg-gray100"
                        opts={{
                            align: "center",
                            skipSnaps: false,
                            dragFree: false,
                        }}
                        className="w-full"
                    >
                        <CarouselContentVisible className="max-md:ml-[-6px] md:ml-[-26px]">
                            {STEPS.map((step, index) => (
                                <CarouselItem
                                    key={index}
                                    className="pl-[6px] md:pl-[26px] flex-shrink-0 basis-full md:basis-1/2 xl:basis-1/3"
                                >
                                    <GettingStartedCarouselStepCard
                                        stepNumber={index + 1}
                                        {...step}
                                    />
                                </CarouselItem>
                            ))}
                        </CarouselContentVisible>
                    </Carousel>
                </div>

                <div className="flex flex-col items-center">
                    <QuizLink
                        variant="primary"
                        href={OESTRA_QUIZ_ENTRY_HREF}
                        eventData={{
                            event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                            widget: "how_it_works",
                            meta_data: {
                                click_text: "Start with oestra®",
                                product_name: "oestra"
                            },
                        }}
                        size="md"
                    >
                        <TextWithRegisteredMark text="start with oestra®" />
                    </QuizLink>
                </div>
            </Container>
        </section>
    );
};

export default GetStartedSection;
